import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  XMarkIcon,
  TruckIcon,
  CalendarIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  EyeIcon,
  PhotoIcon,
  ReceiptRefundIcon,
  DocumentDuplicateIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';

interface Vehicle {
  id?: string;
  make?: string;
  brand?: string;
  model: string;
  licensePlate: string;
  year: string;
  km?: string;
}

interface Service {
  id: string;
  serviceType: string;
  workDescription: string;
  totalCost: number;
  status: string;
  createdAt: string;
  vehicleId?: string;
  clientId?: string;
  mileageAtService?: number;
  photos?: string[];
  issuesFound?: any[];
  partsUsed?: any[];
}

interface Appointment {
  id: string;
  serviceType: string;
  description?: string;
  scheduledDate: string;
  status: string;
  vehicleId?: string;
  clientId?: string;
}

interface Budget {
  id: string;
  description?: string;
  totalAmount: number;
  status: string;
  createdAt: string;
  validUntil?: string;
  vehicleId?: string;
  clientId?: string;
  paymentMethod?: string;
}

interface VehicleHistoryModalProps {
  vehicle: Vehicle;
  clientName: string;
  isOpen: boolean;
  onClose: () => void;
}

const VehicleHistoryModal: React.FC<VehicleHistoryModalProps> = ({ 
  vehicle, 
  clientName, 
  isOpen, 
  onClose 
}) => {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [services, setServices] = useState<Service[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalServices: 0,
    totalSpent: 0,
    totalAppointments: 0,
    completedServices: 0,
    averageServiceCost: 0,
    lastServiceDate: null as string | null
  });

  const panelClass = isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200';
  const modalBg = isDarkMode ? 'bg-gray-900' : 'bg-white';
  const tabsBarClass = isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50';
  const inactiveTab = isDarkMode ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300';
  const textPrimary = isDarkMode ? 'text-gray-100' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const softBg = isDarkMode ? 'bg-gray-700' : 'bg-gray-50';

  useEffect(() => {
    if (isOpen && vehicle.id) {
      fetchVehicleHistory();
    }
  }, [isOpen, vehicle.id]);

  const fetchVehicleHistory = async () => {
    setLoading(true);
    try {
      const [servicesRes, appointmentsRes, budgetsRes] = await Promise.all([
        axios.get(`/api/service-reports?vehicleId=${vehicle.id}`),
        axios.get(`/api/appointments-enhanced?vehicleId=${vehicle.id}`),
        axios.get(`/api/budgets?vehicleId=${vehicle.id}`)
      ]);

      const vehicleServices = servicesRes.data.reports || [];
      const vehicleAppointments = appointmentsRes.data.appointments || [];
      const vehicleBudgets = budgetsRes.data.budgets || [];

      setServices(vehicleServices);
      setAppointments(vehicleAppointments);
      setBudgets(vehicleBudgets);

      // Calcular estadísticas
      const completedServices = vehicleServices.filter((s: Service) => s.status === 'completed');
      const totalSpent = completedServices.reduce((sum: number, s: Service) => sum + s.totalCost, 0);
      const averageCost = completedServices.length > 0 ? totalSpent / completedServices.length : 0;
      const lastService = completedServices.length > 0 
        ? completedServices.sort((a: Service, b: Service) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
        : null;

      setStats({
        totalServices: vehicleServices.length,
        totalSpent,
        totalAppointments: vehicleAppointments.length,
        completedServices: completedServices.length,
        averageServiceCost: averageCost,
        lastServiceDate: lastService?.createdAt || null
      });

    } catch (error) {
      console.error('Error fetching vehicle history:', error);
      toast.error('Error al cargar el historial del vehículo');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'completed': isDarkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800',
      'in_progress': isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800',
      'pending': isDarkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800',
      'cancelled': isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800',
      'approved': isDarkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800',
      'rejected': isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
    };
    return (colors as any)[status] || (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800');
  };

  const getStatusText = (status: string) => {
    const texts = {
      'completed': 'Completado',
      'in_progress': 'En Progreso',
      'pending': 'Pendiente',
      'cancelled': 'Cancelado',
      'approved': 'Aprobado',
      'rejected': 'Rechazado'
    };
    return (texts as any)[status] || status;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden ${modalBg}`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-white">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="bg-white bg-opacity-20 p-3 rounded-full">
                <TruckIcon className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{vehicle.make || vehicle.brand} {vehicle.model}</h2>
                <p className="text-blue-100">
                  {clientName} • Patente: {vehicle.licensePlate} • Año: {vehicle.year}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-100 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className={`border-b ${tabsBarClass}`}>
          <div className="px-6">
            <nav className="flex space-x-8">
              {[
                { id: 'overview', name: 'Resumen', icon: ChartBarIcon },
                { id: 'services', name: 'Servicios', icon: WrenchScrewdriverIcon },
                { id: 'appointments', name: 'Citas', icon: CalendarIcon },
                { id: 'budgets', name: 'Presupuestos', icon: DocumentTextIcon }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : inactiveTab
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Estadísticas */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className={`${panelClass} rounded-lg p-4 shadow-sm`}>
                      <div className="flex items-center">
                        <div className="bg-blue-100 p-2 rounded-full">
                          <WrenchScrewdriverIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="ml-3">
                          <p className={`text-sm font-medium ${textSecondary}`}>Total Servicios</p>
                          <p className={`text-2xl font-bold ${textPrimary}`}>{stats.totalServices}</p>
                        </div>
                      </div>
                    </div>

                    <div className={`${panelClass} rounded-lg p-4 shadow-sm`}>
                      <div className="flex items-center">
                        <div className="bg-green-100 p-2 rounded-full">
                          <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="ml-3">
                          <p className={`text-sm font-medium ${textSecondary}`}>Total Gastado</p>
                          <p className={`text-2xl font-bold ${textPrimary}`}>{formatCurrency(stats.totalSpent)}</p>
                        </div>
                      </div>
                    </div>

                    <div className={`${panelClass} rounded-lg p-4 shadow-sm`}>
                      <div className="flex items-center">
                        <div className="bg-purple-100 p-2 rounded-full">
                          <CalendarIcon className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="ml-3">
                          <p className={`text-sm font-medium ${textSecondary}`}>Citas</p>
                          <p className={`text-2xl font-bold ${textPrimary}`}>{stats.totalAppointments}</p>
                        </div>
                      </div>
                    </div>

                    <div className={`${panelClass} rounded-lg p-4 shadow-sm`}>
                      <div className="flex items-center">
                        <div className="bg-orange-100 p-2 rounded-full">
                          <ClockIcon className="h-5 w-5 text-orange-600" />
                        </div>
                        <div className="ml-3">
                          <p className={`text-sm font-medium ${textSecondary}`}>Último Servicio</p>
                          <p className={`text-sm font-bold ${textPrimary}`}>
                            {stats.lastServiceDate ? formatDate(stats.lastServiceDate) : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Información del vehículo */}
                  <div className={`${panelClass} rounded-lg p-6 shadow-sm`}>
                    <h3 className={`text-lg font-semibold mb-4 ${textPrimary}`}>Información del Vehículo</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className={`text-sm ${textSecondary}`}>Marca y Modelo</p>
                        <p className={`font-medium ${textPrimary}`}>{vehicle.make || vehicle.brand} {vehicle.model}</p>
                      </div>
                      <div>
                        <p className={`text-sm ${textSecondary}`}>Patente</p>
                        <p className={`font-medium ${textPrimary}`}>{vehicle.licensePlate}</p>
                      </div>
                      <div>
                        <p className={`text-sm ${textSecondary}`}>Año</p>
                        <p className={`font-medium ${textPrimary}`}>{vehicle.year}</p>
                      </div>
                      <div>
                        <p className={`text-sm ${textSecondary}`}>Cliente</p>
                        <p className={`font-medium ${textPrimary}`}>{clientName}</p>
                      </div>
                    </div>
                  </div>

                  {/* Servicios recientes */}
                  {services.length > 0 && (
                    <div className={`${panelClass} rounded-lg p-6 shadow-sm`}>
                      <h3 className={`text-lg font-semibold mb-4 ${textPrimary}`}>Servicios Recientes</h3>
                      <div className="space-y-3">
                        {services.slice(0, 3).map((service) => (
                          <div key={service.id} className={`flex justify-between items-center p-3 rounded-lg ${softBg}`}>
                            <div>
                              <p className={`font-medium ${textPrimary}`}>{service.serviceType}</p>
                              <p className={`text-sm ${textSecondary}`}>{formatDate(service.createdAt)}</p>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${textPrimary}`}>{formatCurrency(service.totalCost)}</p>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                                {getStatusText(service.status)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'services' && (
                <div className="space-y-4">
                  <h3 className={`text-lg font-semibold ${textPrimary}`}>Historial de Servicios</h3>
                  {services.length > 0 ? (
                    <div className="space-y-4">
                      {services.map((service) => (
                        <div key={service.id} className={`${panelClass} rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300`}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h4 className={`font-semibold ${textPrimary}`}>{service.serviceType}</h4>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                                  {getStatusText(service.status)}
                                </span>
                              </div>
                              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-2`}>{service.workDescription}</p>
                              <div className={`text-sm ${textSecondary} space-y-1`}>
                                <p>Fecha: {formatDate(service.createdAt)}</p>
                                {service.mileageAtService && (
                                  <p>Kilometraje: {service.mileageAtService.toLocaleString()} km</p>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end space-y-2">
                              <p className={`text-lg font-bold ${textPrimary}`}>{formatCurrency(service.totalCost)}</p>
                              <button
                                onClick={() => {
                                  toast.success('Funcionalidad de archivos próximamente disponible');
                                }}
                                className="flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-sm hover:shadow-md"
                              >
                                <EyeIcon className="h-4 w-4" />
                                <span>Ver Archivos</span>
                              </button>
                            </div>
                          </div>
                          
                          {/* Preview de archivos disponibles */}
                          {service.photos && service.photos.length > 0 && (
                            <div className={`mt-4 pt-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                              <div className={`flex items-center space-x-4 text-xs ${textSecondary}`}>
                                <div className="flex items-center space-x-1">
                                  <PhotoIcon className="h-3 w-3 text-green-500" />
                                  <span>Fotos ({service.photos.length})</span>
                                </div>
                                {service.issuesFound && service.issuesFound.length > 0 && (
                                  <div className="flex items-center space-x-1">
                                    <ReceiptRefundIcon className="h-3 w-3 text-blue-500" />
                                    <span>Problemas ({service.issuesFound.length})</span>
                                  </div>
                                )}
                                {service.partsUsed && service.partsUsed.length > 0 && (
                                  <div className="flex items-center space-x-1">
                                    <DocumentDuplicateIcon className="h-3 w-3 text-purple-500" />
                                    <span>Partes ({service.partsUsed.length})</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <WrenchScrewdriverIcon className={`h-12 w-12 ${textSecondary} mx-auto mb-4`} />
                      <p className={textSecondary}>No hay servicios registrados para este vehículo</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'appointments' && (
                <div className="space-y-4">
                  <h3 className={`text-lg font-semibold ${textPrimary}`}>Historial de Citas</h3>
                  {appointments.length > 0 ? (
                    <div className="space-y-4">
                      {appointments.map((appointment) => (
                        <div key={appointment.id} className={`${panelClass} rounded-lg p-4 shadow-sm`}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h4 className={`font-semibold ${textPrimary}`}>{appointment.serviceType}</h4>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                                  {getStatusText(appointment.status)}
                                </span>
                              </div>
                              {appointment.description && (
                                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-2`}>{appointment.description}</p>
                              )}
                              <div className={`text-sm ${textSecondary}`}>
                                <p>Fecha programada: {new Date(appointment.scheduledDate).toLocaleString('es-AR')}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <CalendarIcon className={`h-12 w-12 ${textSecondary} mx-auto mb-4`} />
                      <p className={textSecondary}>No hay citas registradas para este vehículo</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'budgets' && (
                <div className="space-y-4">
                  <h3 className={`text-lg font-semibold ${textPrimary}`}>Historial de Presupuestos</h3>
                  {budgets.length > 0 ? (
                    <div className="space-y-4">
                      {budgets.map((budget) => (
                        <div key={budget.id} className={`${panelClass} rounded-lg p-4 shadow-sm`}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h4 className={`font-semibold ${textPrimary}`}>
                                  {budget.description || 'Presupuesto'}
                                </h4>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(budget.status)}`}>
                                  {getStatusText(budget.status)}
                                </span>
                              </div>
                              <div className={`text-sm ${textSecondary}`}>
                                <p>Fecha: {formatDate(budget.createdAt)}</p>
                                {budget.validUntil && (
                                  <p>Válido hasta: {formatDate(budget.validUntil)}</p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`text-lg font-bold ${textPrimary}`}>{formatCurrency(budget.totalAmount)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <DocumentTextIcon className={`h-12 w-12 ${textSecondary} mx-auto mb-4`} />
                      <p className={textSecondary}>No hay presupuestos registrados para este vehículo</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleHistoryModal;
